import { useCallback, useDeferredValue, useMemo } from 'react';
import { Baseline, OneOfElements } from '@shared/models/project.model';
import {
  Results,
  ResultsRecord,
  ConversionFactors,
  ConversionFactorGroupKey,
  ConversionFactorUnit,
} from '@shared/models/unit.model';
import {
  getValueFromResultsRecord,
  getProjectResultsRecord,
  getResultsByFactor,
} from '@shared/helpers/results.helpers';
import {
  getBuildingVersionById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { useSelectedVersion, useSelectedVersionId } from '@client/store/ui';
import {
  useProposalResultsRecord,
  useProposalsResultRecords,
} from '@features/proposals/use-proposals.hooks';
import { IProposal } from '@shared/models/proposals.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import {
  useVersions,
  useElementChildren,
} from '../../src/hooks/use-elements.hooks';
import { useProject, useProjectBuildingGFA } from '@client/store/project';
import { getMaxValuesInArray } from '@shared/helpers/math.helpers';
import {
  getCo2eChartDataFromResults,
  getCostChartDataFromResults,
} from '@ui/BarChart/helpers/bar-chart.helpers';
import { ISimpleBarChartValue } from '@ui/BarChart';
import { useSelectedLifecycles } from '@features/lifecycles/LifecyclesSelect';
import { useEffectiveSbefGroups } from '@features/sbef/use-effective-sbef-groups.hook';
import { useConservativeGenericCarbon } from '@features/results/conservative-generic-carbon.hook';
import { filterResultsByScope } from '@shared/helpers/results-scope.helpers';
import { getId, mapObject } from '@shared/helpers/object.helpers';
import { groupBy } from 'lodash';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';
import { getElementGroupingHelpers } from '@features/elements/element-grouping.model';
import { sumConversionFactors } from '@shared/helpers/conversion-factors.helpers';
import { cacheFactory } from '@shared/helpers/function.helpers';

/**
 * Get latest results for each element in the entire project.
 */
export function useProjectResultsRecord(): ResultsRecord {
  const project = useProject();
  const lifecycleFilter = useFilterResultsRecordBySelectedLifecycles();
  return useMemo(
    () => lifecycleFilter(getProjectResultsRecord(project)),
    [project, lifecycleFilter],
  );
}

/**
 * Get latest conversion factors totals for each element for a specific version.
 * @param version The version to get the conversion factors for, if not provided, the selectedVersion is used.
 * @param applyLifecycleFilter If true (default), the results record will be filtered by the selected lifecycles
 */
export function useVersionResultRecord(
  version?: Baseline,
  applyLifecycleFilter: boolean = true,
): ResultsRecord {
  const filterResultsByLifecycles = useFilterResultsBySelectedLifecycles();
  const baselineRecord = version?.resultsRecord;
  const proposalResultsRecord = useProposalResultsRecord(undefined, {
    version,
    applyLifecycleFilter,
  });

  // Use a process-wide shared memo so every consumer (ElementsView, product
  // list hooks, sorting, element groups, etc.) reads the same object reference
  // for identical inputs. Relying on a per-component `useMemo` here forced each
  // caller's `useCallback`-wrapped lifecycle filter into the dep list, so each
  // component produced its own object identity for the same data and busted
  // downstream `useSharedMemo` caches on every render.
  if (baselineRecord) {
    return cacheFactory(
      () => {
        if (!applyLifecycleFilter) return baselineRecord;
        return mapObject(baselineRecord, (results) =>
          filterResultsByLifecycles(results),
        );
      },
      `useVersionResultRecord:${version.id}:${
        applyLifecycleFilter ? 'filtered' : 'raw'
      }`,
      [baselineRecord, filterResultsByLifecycles, applyLifecycleFilter],
    );
  }

  return proposalResultsRecord;
}

type UseVersionResultsOptions = {
  /** If true (default), results are filtered by the selected lifecycles */
  applyLifecycleFilter?: boolean;
};

/**
 * Version-level rollup results from {@link Baseline.resultsRecord}[version.id]
 * (or proposal record when baseline is missing), scoped to the given version.
 * @param versionOrId - Version or id; when omitted, uses {@link useSelectedVersionId}
 * @param options.applyLifecycleFilter - If true (default), filter by selected lifecycles
 */
export const useVersionResults = (
  versionOrId?: ItemOrItemId<Baseline>,
  { applyLifecycleFilter = true }: UseVersionResultsOptions = {},
): Results => {
  const buildingVersions = useVersions();
  const selectedVersionId = useSelectedVersionId();

  const version = useMemo(() => {
    if (versionOrId) {
      if (typeof versionOrId === 'string') {
        return getBuildingVersionById(buildingVersions, versionOrId);
      }
      return versionOrId;
    }
    if (!selectedVersionId) return undefined;
    return getBuildingVersionById(buildingVersions, selectedVersionId);
  }, [buildingVersions, selectedVersionId, versionOrId]);

  const record = useVersionResultRecord(version, applyLifecycleFilter);

  return useMemo(() => {
    if (!version) return {};
    return record[version.id] ?? {};
  }, [record, version]);
};

/**
 * Get function that calculates results per GFA
 * @returns
 */
export const useGetResultsPerGFA = () => {
  const selectedProjectGFA = useProjectBuildingGFA();

  return useCallback(
    <T extends Results | ConversionFactors | number>(
      results: T | undefined,
      gfa = selectedProjectGFA,
    ): T => getResultsByFactor(results, gfa),
    [selectedProjectGFA],
  );
};

/**
 * Get results per GFA
 * @param results
 * @param gfa - optional, if not provided, the selected project GFA is used
 * @returns
 */
export const useResultsPerGFA = <
  T extends Results | ConversionFactors | number,
>(
  results: T = {} as T,
  gfa?: number,
): T => {
  const getResultsPerGFA = useGetResultsPerGFA();
  return useMemo(
    () => getResultsPerGFA(results, gfa),
    [results, getResultsPerGFA, gfa],
  );
};

export const useCo2eChartData = (results: Results): ISimpleBarChartValue[] => {
  return useMemo(() => getCo2eChartDataFromResults(results), [results]);
};

export const useCostChartData = (results: Results): ISimpleBarChartValue[] => {
  return useMemo(() => getCostChartDataFromResults(results), [results]);
};

/**
 * Get the largest results for any active element in the version
 */
export const useElementMaxResults = (): Results => {
  const resultRecords = useProposalsResultRecords();
  const [groupById] = useElementListGroupBy();
  const groupingHelpers = getElementGroupingHelpers(groupById);
  const version = useSelectedVersion();
  const elements = useElementChildren(version);

  return useMemo(() => {
    const recordsArray = Object.values(resultRecords);
    const filterActive = (element: OneOfElements) =>
      !isElement(element) || (!element.isDeactivated && !element.isDeleted);
    const elementResults: Results[] = elements.flatMap(({ id }) =>
      recordsArray.map((r) => r[id] ?? {}),
    );

    // Dynamic group totals (root-level groups) — include in max calculations
    let groupTotals: Results[] = [];
    if (groupingHelpers && elements.length) {
      const activeRoot = elements.filter(filterActive);
      const groups = groupBy(activeRoot, groupingHelpers.helpers.getGroupKey);

      groupTotals = Object.values(groups).flatMap((items) =>
        recordsArray.map((record) =>
          sumConversionFactors(...items.map((el) => record?.[el.id] ?? {})),
        ),
      );
    }

    return getMaxValuesInArray([...elementResults, ...groupTotals]);
  }, [resultRecords, groupingHelpers, elements]);
};

/**
 * Get function that filters results record by selected lifecycles
 * @param results
 * @returns
 */
export const useFilterResultsRecordBySelectedLifecycles = (): ((
  results: ResultsRecord,
) => ResultsRecord) => {
  const filter = useFilterResultsBySelectedLifecycles();

  return useCallback(
    (results: ResultsRecord) => mapObject(results, filter),
    [filter],
  );
};

/**
 * Get a function that applies the full result scope (selected lifecycles **and** selected
 * SBEF groups) to a row. Kept under the historical name because it is the single chokepoint
 * every result-record consumer already uses; see {@link filterResultsByScope}.
 * @param results
 * @returns
 */
export const useFilterResultsBySelectedLifecycles = (): ((
  results?: Results,
) => Results) => {
  // Deferred so a scope change repaints the scope selector immediately and the
  // heavy result consumers (charts, element and product lists) re-render at low
  // priority instead of blocking that commit.
  const selectedLifecycles = useDeferredValue(useSelectedLifecycles());
  const effectiveSbefGroups = useDeferredValue(useEffectiveSbefGroups());
  const conservative = useDeferredValue(useConservativeGenericCarbon());
  return useCallback(
    (results = {}) =>
      filterResultsByScope(results, {
        lifecycles: selectedLifecycles,
        sbefGroups: effectiveSbefGroups,
        conservative,
      }),
    [selectedLifecycles, effectiveSbefGroups, conservative],
  );
};

/** Project list rows carry the first proposal's full result scope, including SBEF buckets. */
export const useFilterProjectListResults = (): ((
  results?: Results,
) => Results) => useFilterResultsBySelectedLifecycles();

/**
 * Filter results by selected lifecycles
 * @param results
 * @returns
 */
export const useResultsBySelectedLifecycles = (
  results: Results | undefined,
) => {
  const filter = useFilterResultsBySelectedLifecycles();
  return useMemo(() => filter(results ?? {}), [filter, results]);
};

/** First-proposal results in the project list use the same scope as the open project. */
export const useProjectListResultsByScope = (results: Results | undefined) => {
  const filter = useFilterProjectListResults();
  return useMemo(() => filter(results ?? {}), [filter, results]);
};

/**
 * Use results for a specific element from the active proposal's {@link ResultsRecord}.
 * Prefers proposal data when available via {@link useProposalResultsRecord}.
 * @param elementOrId
 * @param inProposal - Specific proposal to read from (defaults to active)
 * @param applyLifecycleFilter - If true (default), the results will be filtered by the selected lifecycles
 * @returns
 */
export const useElementResults = (
  elementOrId: ItemOrItemId<OneOfElements> | undefined,
  inProposal?: ItemOrItemId<IProposal>,
  applyLifecycleFilter: boolean = true,
): Results => {
  const id = elementOrId ? getId(elementOrId) : undefined;
  const record = useProposalResultsRecord(inProposal, { applyLifecycleFilter });

  return useMemo(() => (id && record[id] ? record[id] : {}), [id, record]);
};

export const useProjectVersionsMax = (
  unit: ConversionFactorUnit | ConversionFactorGroupKey = 'co2e',
): number => {
  const versions = useVersions();
  const projectsTotals = useProjectResultsRecord();
  return useMemo(() => {
    const versionFactors = versions.map((version) =>
      getValueFromResultsRecord(projectsTotals, version.id, unit),
    );
    return Math.max(...versionFactors, 0);
  }, [projectsTotals, unit, versions]);
};
